import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";

import { PageLoader } from "@/components/ui/skeleton-loaders";
import PageTransition from "@/components/PageTransition";
import { HelmetProvider } from "react-helmet-async";
import LanguageRouter from "@/components/LanguageRouter";
import Index from "./pages/Index";

// Lazy load non-critical routes
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Legal = lazy(() => import("./pages/Legal"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminResetPassword = lazy(() => import("./pages/AdminResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));

import CookieConsent from "@/components/CookieConsent";
import { RootRedirect } from "@/components/RootRedirect";

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <PageTransition>
                  <Routes>
                    {/* Root redirect to /:lang */}
                    <Route path="/" element={<RootRedirect />} />

                    {/* Localized routes */}
                    <Route path="/:lang" element={<LanguageRouter />}>
                      <Route index element={<Index />} />
                      <Route path="blog" element={<Blog />} />
                      <Route path="blog/:slug" element={<BlogPost />} />
                      <Route path="legal" element={<Legal />} />
                    </Route>

                    {/* Non-localized routes */}
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/admin/reset-password" element={<AdminResetPassword />} />

                    {/* Legacy redirects (old URLs without lang prefix) */}
                    <Route path="/blog" element={<Navigate to="/fr/blog" replace />} />
                    <Route path="/blog/:slug" element={<LegacyBlogRedirect />} />
                    <Route path="/legal" element={<Navigate to="/fr/legal" replace />} />

                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  <CookieConsent />
                </PageTransition>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </LanguageProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

/** Redirect /blog/:slug to /fr/blog/:slug */
function LegacyBlogRedirect() {
  const params = new URL(window.location.href);
  const slug = window.location.pathname.replace("/blog/", "");
  return <Navigate to={`/fr/blog/${slug}`} replace />;
}

export default App;
